import { AnimatePresence, Variants, motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { Breakpoint } from '../helpers';
import { CloseCircleIcon } from '../icons';
import { IconButton } from './button/IconButton';

type Props = {
	children: React.ReactNode;
	onClose: () => void;
	open: boolean;
	width?: string | number;
	ariaLabel: string;
	closeLabel: string;
};

/** Overlay / panel stack. Popovers used inside a drawer must render above this. */
export const DRAWER_Z_INDEX = 1000;
const OVERLAY_Z_INDEX = DRAWER_Z_INDEX;
const DEFAULT_WIDTH = 400;

const FOCUSABLE_SELECTOR =
	'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Reference-count open drawers so that with stacked drawers, closing one does
// not un-inert the app or restore body scrolling while another is still open.
let openDrawerCount = 0;
let previousBodyOverflow = '';

const lockBackground = () => {
	openDrawerCount += 1;

	if (openDrawerCount > 1) return;

	previousBodyOverflow = document.body.style.overflow;
	document.body.style.overflow = 'hidden';
	document.getElementById('root')?.setAttribute('inert', '');
};

const unlockBackground = () => {
	openDrawerCount -= 1;

	if (openDrawerCount > 0) return;

	document.body.style.overflow = previousBodyOverflow;
	document.getElementById('root')?.removeAttribute('inert');
};

const overlayVariants: Variants = {
	hidden: { opacity: 0 },
	visible: { opacity: 1 },
};

const panelVariants: Variants = {
	hidden: { x: '100%', opacity: 0 },
	visible: { x: 0, opacity: 1 },
};

const Overlay = styled(motion.div)`
	position: fixed;
	inset: 0;
	background-color: rgba(0, 0, 0, 0.5);
	z-index: ${OVERLAY_Z_INDEX};
`;

const Panel = styled(motion.div)<{ $width: string | number }>`
	position: fixed;
	top: 0;
	right: 0;
	height: 100%;
	width: ${(props) => (typeof props.$width === 'number' ? `${props.$width}px` : props.$width)};
	background-color: ${(props) => props.theme.colors.white};
	z-index: ${OVERLAY_Z_INDEX + 1};
	outline: none;

	@media screen and (max-width: ${Breakpoint.Small}) {
		width: 90dvw;
	}
`;

const CloseButtonContainer = styled.div`
	position: absolute;
	top: 12px;
	right: 12px;
`;

export const Drawer = ({
	children,
	onClose,
	open,
	width = DEFAULT_WIDTH,
	ariaLabel,
	closeLabel,
}: Props) => {
	const panelRef = useRef<HTMLDivElement>(null);

	// Keep the latest onClose in a ref so an unstable callback from the caller
	// does not tear down and re-run the focus/scroll-lock effect on every render.
	const onCloseRef = useRef(onClose);
	useEffect(() => {
		onCloseRef.current = onClose;
	}, [onClose]);

	useEffect(() => {
		if (!open) return;

		const previousFocus = document.activeElement as HTMLElement | null;
		lockBackground();

		panelRef.current?.focus();

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				if (event.isComposing || event.defaultPrevented) return;
				event.preventDefault();
				onCloseRef.current();
				return;
			}

			if (event.key !== 'Tab' || !panelRef.current) return;

			const focusable = Array.from(
				panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
			).filter((element) => element.offsetParent !== null);

			// Keep focus inside the drawer while it is open
			if (focusable.length === 0) {
				event.preventDefault();
				panelRef.current.focus();
				return;
			}

			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			const active = document.activeElement;

			if (event.shiftKey && (active === first || active === panelRef.current)) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && active === last) {
				event.preventDefault();
				first.focus();
			}
		};

		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			unlockBackground();
			if (previousFocus && document.contains(previousFocus)) {
				previousFocus.focus({ preventScroll: true });
			}
		};
	}, [open]);

	if (typeof document === 'undefined') return null;

	return createPortal(
		<AnimatePresence>
			{open && (
				<motion.div key="drawer" initial="hidden" animate="visible" exit="hidden">
					<Overlay
						variants={overlayVariants}
						transition={{ duration: 0.25, ease: 'easeOut' }}
						onClick={onClose}
						aria-hidden="true"
					/>
					<Panel
						ref={panelRef}
						role="dialog"
						aria-modal="true"
						aria-label={ariaLabel}
						tabIndex={-1}
						variants={panelVariants}
						transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
						$width={width}
					>
						<CloseButtonContainer>
							<IconButton
								type="plain"
								small
								icon={<CloseCircleIcon />}
								onClick={onClose}
								aria-label={closeLabel}
							/>
						</CloseButtonContainer>
						{children}
					</Panel>
				</motion.div>
			)}
		</AnimatePresence>,
		document.body,
	);
};
